import { displayHost } from "../display-host";
import { normalizeExternalUrl } from "../external-url";

type PolicyTextSegment =
    | { kind: "text"; text: string }
    | { kind: "link"; href: string; label: string };

export type BarePolicyUrl = { href: string; host: string };

export type PolicyView = {
    id: string;
    label: string;
    text: string;
    bareUrl: BarePolicyUrl | null;
};

type PolicySource = {
    id: string;
    type: string;
    title: string | null;
    text: string;
};

const POLICY_LABELS: Record<string, string> = {
    transport: "Pickup and transport",
    spay_neuter: "Spay and neuter",
};

export type PreviewClamp =
    | { lines: 4; className: "line-clamp-4" }
    | { lines: 3; className: "line-clamp-3" };

const PREVIEW_CHARS_PER_LINE = 30;
const ALWAYS_OFFER_SHEET_FROM = 3;

const URL_PATTERN = /https?:\/\/[^\s<>"']+/giu;
const HOSTNAME_SHAPE = /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/iu;
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", "]", ")"]);
const OPENING_BRACKET: Record<string, string> = { ")": "(", "]": "[" };

function toHttpUrl(value: string): URL | null {
    try {
        const url = new URL(value);
        if (url.protocol !== "http:" && url.protocol !== "https:") return null;
        if (url.username || url.password) return null;
        return HOSTNAME_SHAPE.test(url.hostname) ? url : null;
    } catch {
        return null;
    }
}

function count(text: string, character: string): number {
    return text.split(character).length - 1;
}

function stripTrailingPunctuation(url: string): string {
    let end = url.length;
    while (end > 0) {
        const last = url.charAt(end - 1);
        if (!TRAILING_PUNCTUATION.has(last)) break;
        const head = url.slice(0, end);
        const opening = OPENING_BRACKET[last];
        if (opening && count(head, last) <= count(head, opening)) break;
        end -= 1;
    }
    return url.slice(0, end);
}

export function policyLabel(type: string, isBreeder: boolean): string {
    if (type === "adoption") return isBreeder ? "Buying a puppy" : "Adoption";
    const known = POLICY_LABELS[type];
    if (known) return known;
    const words = type.replaceAll("_", " ").trim();
    return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

export function splitParagraphs(text: string): string[] {
    return text
        .replaceAll("\r\n", "\n")
        .trim()
        .split(/\n\s*\n/u)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
}

export function collapseBlankLines(text: string): string {
    return splitParagraphs(text).join("\n");
}

export function parseBarePolicyUrl(text: string): BarePolicyUrl | null {
    const trimmed = text.trim();
    if (!trimmed || /\s/u.test(trimmed)) return null;
    const stripped = stripTrailingPunctuation(trimmed);
    const hasScheme = trimmed.includes("://");
    const endsLikeASentence = stripped !== trimmed;
    if (!hasScheme && endsLikeASentence) return null;
    const url = toHttpUrl(
        normalizeExternalUrl(hasScheme ? stripped : trimmed) ?? "",
    );
    return url
        ? { href: url.toString(), host: displayHost(url.hostname) }
        : null;
}

export function toPolicyViews(
    policies: readonly PolicySource[],
    isBreeder: boolean,
): PolicyView[] {
    return policies.map((policy) => ({
        id: policy.id,
        label: policy.title?.trim() || policyLabel(policy.type, isBreeder),
        text: policy.text,
        bareUrl: parseBarePolicyUrl(policy.text),
    }));
}

export function linkifyPolicyText(text: string): PolicyTextSegment[] {
    const segments: PolicyTextSegment[] = [];
    let cursor = 0;
    for (const match of text.matchAll(URL_PATTERN)) {
        const matched = stripTrailingPunctuation(match[0]);
        const url = toHttpUrl(matched);
        if (!url) continue;
        if (match.index > cursor) {
            segments.push({
                kind: "text",
                text: text.slice(cursor, match.index),
            });
        }
        segments.push({
            kind: "link",
            href: url.toString(),
            label: matched.replace(/^https?:\/\/(?:www\.)?/iu, ""),
        });
        cursor = match.index + matched.length;
    }
    if (cursor < text.length) {
        segments.push({ kind: "text", text: text.slice(cursor) });
    }
    return segments;
}

export function previewClamp(policyCount: number): PreviewClamp {
    return policyCount === 1
        ? { lines: 4, className: "line-clamp-4" }
        : { lines: 3, className: "line-clamp-3" };
}

function estimatePolicyLines(text: string): number {
    return collapseBlankLines(text)
        .split("\n")
        .reduce(
            (total, line) =>
                total +
                Math.max(1, Math.ceil(line.length / PREVIEW_CHARS_PER_LINE)),
            0,
        );
}

export function shouldOfferPolicySheet(
    policies: readonly Pick<PolicyView, "text" | "bareUrl">[],
): boolean {
    if (policies.length >= ALWAYS_OFFER_SHEET_FROM) return true;
    const { lines } = previewClamp(policies.length);
    return policies.some(
        (policy) => !policy.bareUrl && estimatePolicyLines(policy.text) > lines,
    );
}
